import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookmarkPlus, Library } from 'lucide-react';
import { useIpeAuth } from '@/context/IpeAuthContext';
import { aiGoodsStudioPromptCaseAPI, fundingAuthAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import GoodsParamSelect from './GoodsParamSelect';
import { RatioWireIcon } from './WorkflowAspectRatioField';
import GoodsStudioPromptCaseLibraryModal from './GoodsStudioPromptCaseLibraryModal';
import GoodsStudioPromptCaseFormModal from '@/pages/ipe/workbench/modals/GoodsStudioPromptCaseFormModal';
import IpeDeleteConfirmModal from '@/pages/ipe/workbench/modals/IpeDeleteConfirmModal';
import WorkflowModelImageUpload from './WorkflowModelImageUpload';
import {
  GOODS_STUDIO_COUNTRY_OPTIONS,
  GOODS_STUDIO_MAIN_IMAGE_RATIO_OPTIONS,
  GOODS_STUDIO_LANGUAGE_OPTIONS,
  GOODS_STUDIO_PRODUCT_INFO_MAX,
  GOODS_STUDIO_DEFAULT_PARAMS,
  GOODS_STUDIO_CATEGORY_OPTIONS,
  GOODS_STUDIO_MODEL_MODE_OPTIONS,
  GOODS_STUDIO_COMPOSITION_OPTIONS,
  GOODS_STUDIO_BACKGROUND_STYLE_OPTIONS,
  getDefaultGoodsStudioCountryStyle,
  getGoodsStudioCountryCopy,
  getGoodsStudioCountryStyles,
  normalizeGoodsStudioCountryStyle,
  normalizeGoodsStudioParams,
  resolveGoodsStudioModelType,
} from '@/pages/ipe/workbench/constants/aiStudioConfig';
import { resolveDefaultTitlePresetScope } from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import { buildTeamOwnerNameMap } from '@/pages/ipe/workbench/utils/aiCopywritingTemplateLoader';
import { toIpeUserErrorMessage } from '@/pages/ipe/workbench/utils/errorHandler';
import { mapGoodsStudioPromptCaseApiItem } from '@/pages/ipe/workbench/utils/goodsStudioPromptCaseLoader';
import { isTeamSpaceEntryAvailable } from '@/pages/ipe/workbench/utils/accountCenterUtils';
import { buildTryOnUploadedModelSelectionParams } from '@/pages/ipe/workbench/utils/tryOnSystemModelPresetFallback';

function parseRatioParts(value) {
  const [wRaw, hRaw] = String(value || '1:1').split(':');
  const w = Math.max(1, Number(wRaw) || 1);
  const h = Math.max(1, Number(hRaw) || 1);
  return { w, h };
}

export default function GoodsStudioSidebarToolbar({
  params,
  onChange,
  disabled = false,
  onCaseOverlayChange,
  /** 模板库 / 新增模板改用居中弹窗（工作流节点参数面板内使用，与 AI 文案一致） */
  overlayCentered = false,
}) {
  const { t } = useTranslation('workbench');
  const { user } = useIpeAuth();
  const workspaceMode = useIpeWorkspaceMode();
  const currentUserId = user?.id != null ? String(user.id) : (user?._id != null ? String(user._id) : '');

  const [openField, setOpenField] = useState(null);
  const [canAccessTeamPresets, setCanAccessTeamPresets] = useState(false);
  const [teamOwnerNameByUserId, setTeamOwnerNameByUserId] = useState({});
  const [caseListNonce, setCaseListNonce] = useState(0);
  const [caseLibraryOpen, setCaseLibraryOpen] = useState(false);
  const [caseLibraryScope, setCaseLibraryScope] = useState(() => (
    resolveDefaultTitlePresetScope(workspaceMode, false)
  ));
  const [caseFormOpen, setCaseFormOpen] = useState(false);
  const [caseFormMode, setCaseFormMode] = useState('create');
  const [caseFormScope, setCaseFormScope] = useState('personal');
  const [caseFormEditing, setCaseFormEditing] = useState(null);
  const [caseFormDefaults, setCaseFormDefaults] = useState(null);
  const [caseFormKey, setCaseFormKey] = useState(0);
  const [caseFormSaving, setCaseFormSaving] = useState(false);
  const [caseDeleteTarget, setCaseDeleteTarget] = useState(null);
  const [caseDeleting, setCaseDeleting] = useState(false);

  const selectedCaseId = String(params.goodsStudioPromptCaseId || '').trim();
  const countryStyle = normalizeGoodsStudioCountryStyle(params.country, params.countryStyle);
  const countryStyles = useMemo(
    () => getGoodsStudioCountryStyles(params.country),
    [params.country],
  );
  const countryCopy = useMemo(
    () => getGoodsStudioCountryCopy(params.country, countryStyle),
    [params.country, countryStyle],
  );
  const goodsCategory = params.goodsCategory || GOODS_STUDIO_DEFAULT_PARAMS.goodsCategory;
  const modelMode = params.modelMode || GOODS_STUDIO_DEFAULT_PARAMS.modelMode;
  const composition = params.composition || GOODS_STUDIO_DEFAULT_PARAMS.composition;
  const backgroundStyle = params.backgroundStyle || GOODS_STUDIO_DEFAULT_PARAMS.backgroundStyle;
  const modelType = params.modelType || resolveGoodsStudioModelType(goodsCategory);

  const patchParams = useCallback((patch) => {
    onChange?.(normalizeGoodsStudioParams({ ...params, ...patch }));
  }, [onChange, params]);

  const caseOverlayOpen = caseLibraryOpen || caseFormOpen || Boolean(caseDeleteTarget);
  useEffect(() => {
    onCaseOverlayChange?.(caseOverlayOpen);
  }, [caseOverlayOpen, onCaseOverlayChange]);

  useEffect(() => {
    let cancelled = false;
    fundingAuthAPI.getAccountCenter()
      .then(async (center) => {
        if (cancelled) return;
        const available = isTeamSpaceEntryAvailable(center);
        setCanAccessTeamPresets(available);
        setCaseLibraryScope((prev) => {
          const preferred = resolveDefaultTitlePresetScope(workspaceMode, available);
          if (prev === 'team' && !available) return preferred;
          return prev || preferred;
        });
        if (!available) {
          setTeamOwnerNameByUserId({});
          return;
        }
        try {
          const overview = await fundingAuthAPI.getTeamOverview();
          if (!cancelled) {
            setTeamOwnerNameByUserId(buildTeamOwnerNameMap(overview?.members));
          }
        } catch {
          if (!cancelled) setTeamOwnerNameByUserId({});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCanAccessTeamPresets(false);
          setTeamOwnerNameByUserId({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceMode]);

  const bumpCaseList = useCallback(() => {
    setCaseListNonce((n) => n + 1);
  }, []);

  const openCaseFormCreate = useCallback((scope, options = {}) => {
    setCaseLibraryOpen(false);
    setCaseFormMode('create');
    setCaseFormScope(scope === 'team' ? 'team' : 'personal');
    setCaseFormEditing(null);
    setCaseFormDefaults(options.prefillFromParams
      ? {
        productInfo: String(params.productInfo || '').trim(),
        country: params.country || GOODS_STUDIO_DEFAULT_PARAMS.country,
        countryStyle: normalizeGoodsStudioCountryStyle(
          params.country || GOODS_STUDIO_DEFAULT_PARAMS.country,
          params.countryStyle,
        ),
        language: params.language || GOODS_STUDIO_DEFAULT_PARAMS.language,
        mainImageRatio: params.mainImageRatio || GOODS_STUDIO_DEFAULT_PARAMS.mainImageRatio,
      }
      : null);
    setCaseFormKey((k) => k + 1);
    setCaseFormOpen(true);
  }, [params]);

  const openCaseFormEdit = useCallback((item) => {
    if (!item?.canEdit) {
      showToast(t('studioToast.noPermEdit'), 'warning');
      return;
    }
    setCaseLibraryOpen(false);
    setCaseFormMode('edit');
    setCaseFormScope(item.scope === 'team' ? 'team' : 'personal');
    setCaseFormEditing(item);
    setCaseFormDefaults(null);
    setCaseFormKey((k) => k + 1);
    setCaseFormOpen(true);
  }, [t]);

  const openCaseDelete = useCallback((item) => {
    if (!item?.canDelete) {
      showToast(t('studioToast.noPermDelete'), 'warning');
      return;
    }
    setCaseDeleteTarget(item);
  }, [t]);

  const handleCaseDeleteConfirm = useCallback(async () => {
    const target = caseDeleteTarget;
    if (!target?.id) return;
    setCaseDeleting(true);
    try {
      await aiGoodsStudioPromptCaseAPI.delete(target.id);
      showToast(t('studioToast.templateDeleted'), 'success');
      setCaseDeleteTarget(null);
      if (caseFormEditing && String(caseFormEditing.id) === String(target.id)) {
        setCaseFormOpen(false);
        setCaseFormEditing(null);
      }
      if (selectedCaseId === String(target.id)) {
        patchParams({ goodsStudioPromptCaseId: '', goodsStudioPromptCaseScope: '' });
      }
      bumpCaseList();
    } catch (e) {
      showToast(toIpeUserErrorMessage(e, t('studioToast.templateDeleteFailed')), 'error');
    } finally {
      setCaseDeleting(false);
    }
  }, [caseDeleteTarget, caseFormEditing, selectedCaseId, patchParams, bumpCaseList, t]);

  const applyCase = useCallback((item) => {
    if (!item) return;
    const nextCountry = item.country || GOODS_STUDIO_DEFAULT_PARAMS.country;
    patchParams({
      goodsStudioPromptCaseId: String(item.id || ''),
      goodsStudioPromptCaseScope: item.scope || '',
      productInfo: String(item.productInfo || '').slice(0, GOODS_STUDIO_PRODUCT_INFO_MAX),
      country: nextCountry,
      countryStyle: normalizeGoodsStudioCountryStyle(nextCountry, item.countryStyle),
      language: item.language || GOODS_STUDIO_DEFAULT_PARAMS.language,
      mainImageRatio: item.mainImageRatio || GOODS_STUDIO_DEFAULT_PARAMS.mainImageRatio,
    });
    showToast(t('studioToast.templateApplied'), 'success');
  }, [patchParams, t]);

  const handleCaseFormSubmit = useCallback(async (payload) => {
    setCaseFormSaving(true);
    try {
      let savedCase = null;
      if (caseFormMode === 'edit' && caseFormEditing?.id) {
        const { caseScope: _scope, ...updateBody } = payload;
        const res = await aiGoodsStudioPromptCaseAPI.update(caseFormEditing.id, updateBody);
        savedCase = mapGoodsStudioPromptCaseApiItem(res?.case);
        showToast(t('studioToast.templateSaved'), 'success');
      } else {
        const res = await aiGoodsStudioPromptCaseAPI.create(payload);
        savedCase = mapGoodsStudioPromptCaseApiItem(res?.case);
        showToast(t('studioToast.templateCreated'), 'success');
      }
      if (savedCase) {
        const nextCountry = savedCase.country || GOODS_STUDIO_DEFAULT_PARAMS.country;
        patchParams({
          goodsStudioPromptCaseId: String(savedCase.id || ''),
          goodsStudioPromptCaseScope: savedCase.scope || '',
          productInfo: String(savedCase.productInfo || '').slice(0, GOODS_STUDIO_PRODUCT_INFO_MAX),
          country: nextCountry,
          countryStyle: normalizeGoodsStudioCountryStyle(nextCountry, savedCase.countryStyle),
          language: savedCase.language || GOODS_STUDIO_DEFAULT_PARAMS.language,
          mainImageRatio: savedCase.mainImageRatio || GOODS_STUDIO_DEFAULT_PARAMS.mainImageRatio,
        });
      }
      setCaseFormOpen(false);
      setCaseFormEditing(null);
      setCaseFormDefaults(null);
      if (payload.caseScope === 'personal' || payload.caseScope === 'team') {
        setCaseLibraryScope(payload.caseScope);
      }
      bumpCaseList();
    } catch (e) {
      showToast(toIpeUserErrorMessage(e, t('studioToast.templateSaveFailed')), 'error');
    } finally {
      setCaseFormSaving(false);
    }
  }, [caseFormMode, caseFormEditing, patchParams, bumpCaseList, t]);

  const openLibrary = () => {
    if (caseLibraryOpen) {
      setCaseLibraryOpen(false);
      return;
    }
    if (caseFormOpen) setCaseFormOpen(false);
    setCaseLibraryScope(
      resolveDefaultTitlePresetScope(workspaceMode, canAccessTeamPresets),
    );
    setCaseLibraryOpen(true);
  };

  const openCreate = () => {
    if (caseFormOpen && caseFormMode === 'create') {
      setCaseFormOpen(false);
      return;
    }
    openCaseFormCreate(
      resolveDefaultTitlePresetScope(workspaceMode, canAccessTeamPresets) === 'team'
        ? 'team'
        : 'personal',
      { prefillFromParams: true },
    );
  };

  const handleProductInfoChange = (event) => {
    const next = event.target.value.slice(0, GOODS_STUDIO_PRODUCT_INFO_MAX);
    patchParams({ productInfo: next });
  };

  const handleCategoryChange = (value) => {
    const nextModelType = resolveGoodsStudioModelType(value);
    patchParams({
      goodsCategory: value,
      modelType: nextModelType,
    });
  };

  const handleModelModeChange = (value) => {
    const patch = { modelMode: value };
    if (value === 'custom') {
      Object.assign(patch, {
        selectedPresetId: '',
        selectedPresetUrl: '',
      });
    } else {
      Object.assign(patch, {
        usedUploadedModel: false,
        modelImageUrl: '',
        modelImagePreviewUrl: '',
        selectedPresetId: '',
        selectedPresetUrl: '',
      });
    }
    patchParams(patch);
  };

  const handleUploadedModelChange = (modelFields) => {
    patchParams({
      ...modelFields,
      selectedPresetId: '',
      selectedPresetUrl: '',
      modelMode: 'custom',
    });
  };

  const clearUploadedModel = () => {
    patchParams({
      usedUploadedModel: false,
      modelImageUrl: '',
      modelImagePreviewUrl: '',
      selectedPresetId: '',
      selectedPresetUrl: '',
    });
  };

  const bindOpen = (field) => ({
    open: openField === field,
    onOpenChange: (next) => setOpenField(next ? field : null),
  });

  return (
    <div className="goods-studio__toolbar">
      <section className="img2img-studio__section goods-studio__info-section">
        <div className="img2img-studio__field-row">
          <label className="img2img-studio__field-label" htmlFor="goods-studio-product-info">
            {t('workflowParams.extraInfo')}
          </label>
          {!disabled ? (
            <div className="studio-case-actions">
              <button type="button" className="studio-case-btn" onClick={openLibrary}>
                <Library size={14} aria-hidden="true" />
                {t('workflowParams.pickTemplate')}
              </button>
              <button type="button" className="studio-case-btn" onClick={openCreate}>
                <BookmarkPlus size={14} aria-hidden="true" />
                {t('workflowParams.newTemplate')}
              </button>
            </div>
          ) : null}
        </div>
        <div className="img2img-studio__prompt-wrap">
          <textarea
            id="goods-studio-product-info"
            className="img2img-studio__prompt"
            value={params.productInfo || ''}
            onChange={handleProductInfoChange}
            placeholder={t('workflowParams.productInfoPh')}
            maxLength={GOODS_STUDIO_PRODUCT_INFO_MAX}
            disabled={disabled}
          />
          <span className="img2img-studio__prompt-count" aria-live="polite">
            {(params.productInfo || '').length}/{GOODS_STUDIO_PRODUCT_INFO_MAX}
          </span>
        </div>
      </section>

      <section className="img2img-studio__section goods-studio__params-section" aria-label={t('workflowParams.groupParams')}>
        <div className="goods-studio__style-field">
          <span className="goods-studio__count-label">{t('workflowParams.garment')}</span>
          <div className="goods-studio__style-chips" role="group" aria-label={t('workflowParams.garment')}>
            {GOODS_STUDIO_CATEGORY_OPTIONS.map((item) => {
              const active = item.value === goodsCategory;
              return (
                <button
                  key={item.value}
                  type="button"
                  className={`goods-studio__style-chip${active ? ' is-active' : ''}`}
                  disabled={disabled}
                  aria-pressed={active}
                  onClick={() => {
                    if (disabled || active) return;
                    setOpenField(null);
                    handleCategoryChange(item.value);
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="goods-studio__style-field">
          <span className="goods-studio__count-label">{t('workflowParams.modelMode')}</span>
          <div className="goods-studio__style-chips" role="group" aria-label={t('workflowParams.modelMode')}>
            {GOODS_STUDIO_MODEL_MODE_OPTIONS.map((item) => {
              const active = item.value === modelMode;
              return (
                <button
                  key={item.value}
                  type="button"
                  className={`goods-studio__style-chip${active ? ' is-active' : ''}`}
                  disabled={disabled}
                  aria-pressed={active}
                  onClick={() => {
                    if (disabled || active) return;
                    setOpenField(null);
                    handleModelModeChange(item.value);
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {modelMode === 'custom' ? (
          <div className="goods-studio__model-upload">
            <WorkflowModelImageUpload
              modelImageUrl={params.modelImageUrl || ''}
              modelImagePreviewUrl={params.modelImagePreviewUrl || ''}
              usedUploadedModel={params.usedUploadedModel === true}
              disabled={disabled}
              onChange={handleUploadedModelChange}
            />
            {params.usedUploadedModel ? (
              <button
                type="button"
                className="studio-case-btn goods-studio__clear-model-btn"
                disabled={disabled}
                onClick={clearUploadedModel}
              >
                {t('goodsStudioToolbar.removeModelImage')}
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="goods-studio__style-field">
          <span className="goods-studio__count-label">{t('workflowParams.bgStyle')}</span>
          <div className="goods-studio__style-chips" role="group" aria-label={t('workflowParams.bgStyle')}>
            {GOODS_STUDIO_BACKGROUND_STYLE_OPTIONS.map((item) => {
              const active = item.value === backgroundStyle;
              return (
                <button
                  key={item.value}
                  type="button"
                  className={`goods-studio__style-chip${active ? ' is-active' : ''}`}
                  disabled={disabled}
                  aria-pressed={active}
                  onClick={() => {
                    if (disabled || active) return;
                    setOpenField(null);
                    patchParams({ backgroundStyle: item.value });
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="goods-studio__style-field">
          <span className="goods-studio__count-label">{t('workflowParams.framing')}</span>
          <div className="goods-studio__style-chips" role="group" aria-label={t('workflowParams.framing')}>
            {GOODS_STUDIO_COMPOSITION_OPTIONS.map((item) => {
              const active = item.value === composition;
              return (
                <button
                  key={item.value}
                  type="button"
                  className={`goods-studio__style-chip${active ? ' is-active' : ''}`}
                  disabled={disabled}
                  aria-pressed={active}
                  onClick={() => {
                    if (disabled || active) return;
                    setOpenField(null);
                    patchParams({ composition: item.value });
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="goods-studio__country-field">
          <div className="goods-studio__count-row">
            <span className="goods-studio__count-label">{t('workflowParams.country')}</span>
            <GoodsParamSelect
              value={params.country}
              options={GOODS_STUDIO_COUNTRY_OPTIONS}
              onChange={(value) => patchParams({
                country: value,
                countryStyle: getDefaultGoodsStudioCountryStyle(value),
              })}
              disabled={disabled}
              ariaLabel={t('workflowParams.country')}
              className="goods-studio__count-select goods-studio__count-select--wide"
              {...bindOpen('country')}
            />
          </div>
          {countryStyles.length > 0 ? (
            <div className="goods-studio__style-field">
              <span className="goods-studio__count-label">{t('workflowParams.marketStyle')}</span>
              <div className="goods-studio__style-chips" role="group" aria-label={t('workflowParams.marketStyle')}>
                {countryStyles.map((item) => {
                  const active = item.value === countryStyle;
                  return (
                    <button
                      key={item.value}
                      type="button"
                      className={`goods-studio__style-chip${active ? ' is-active' : ''}`}
                      disabled={disabled}
                      aria-pressed={active}
                      onClick={() => {
                        if (disabled || active) return;
                        setOpenField(null);
                        patchParams({ countryStyle: item.value });
                      }}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          {countryCopy ? (
            <p className="goods-studio__country-copy" aria-live="polite">
              {countryCopy}
            </p>
          ) : null}
        </div>
        <div className="goods-studio__count-row">
          <span className="goods-studio__count-label">{t('workflowParams.language')}</span>
          <GoodsParamSelect
            value={params.language}
            options={GOODS_STUDIO_LANGUAGE_OPTIONS}
            onChange={(value) => patchParams({ language: value })}
            disabled={disabled}
            ariaLabel={t('workflowParams.language')}
            className="goods-studio__count-select goods-studio__count-select--wide"
            {...bindOpen('language')}
          />
        </div>

        <div className="goods-studio__ratio-field">
          <span className="goods-studio__count-label">{t('workflowParams.imageRatio')}</span>
          <div className="goods-studio__ratio-grid" role="listbox" aria-label={t('workflowParams.imageRatio')}>
            {GOODS_STUDIO_MAIN_IMAGE_RATIO_OPTIONS.map((item) => {
              const active = item.value === params.mainImageRatio;
              const { w, h } = parseRatioParts(item.value);
              return (
                <button
                  key={item.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`goods-studio__ratio-item${active ? ' is-active' : ''}`}
                  disabled={disabled}
                  onClick={() => {
                    if (disabled || active) return;
                    setOpenField(null);
                    patchParams({ mainImageRatio: item.value });
                  }}
                >
                  <span className="goods-studio__ratio-preview" aria-hidden="true">
                    <RatioWireIcon w={w} h={h} />
                  </span>
                  <span className="goods-studio__ratio-text">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <GoodsStudioPromptCaseLibraryModal
        isOpen={caseLibraryOpen}
        onClose={() => setCaseLibraryOpen(false)}
        selectedId={selectedCaseId}
        initialScope={caseLibraryScope}
        canAccessTeamPresets={canAccessTeamPresets}
        currentUserId={currentUserId}
        ownerNameByUserId={teamOwnerNameByUserId}
        centered={overlayCentered}
        onSelect={applyCase}
        onRequestCreate={(scope) => {
          setCaseLibraryOpen(false);
          openCaseFormCreate(scope === 'team' ? 'team' : 'personal');
        }}
        onRequestEdit={(item) => {
          setCaseLibraryOpen(false);
          openCaseFormEdit(item);
        }}
        onRequestDelete={openCaseDelete}
        escapeDisabled={caseFormOpen || Boolean(caseDeleteTarget)}
        refreshNonce={caseListNonce}
      />

      <GoodsStudioPromptCaseFormModal
        key={caseFormKey}
        isOpen={caseFormOpen}
        mode={caseFormMode}
        initialScope={caseFormScope}
        caseItem={caseFormEditing}
        defaults={caseFormDefaults}
        canAccessTeamPresets={canAccessTeamPresets}
        saving={caseFormSaving}
        centered={overlayCentered}
        onClose={() => {
          if (caseFormSaving) return;
          setCaseFormOpen(false);
          setCaseFormEditing(null);
          setCaseFormDefaults(null);
        }}
        onSubmit={handleCaseFormSubmit}
      />

      <IpeDeleteConfirmModal
        open={Boolean(caseDeleteTarget)}
        title={t('promptCaseLib.deleteTitle')}
        confirming={caseDeleting}
        confirmDisabled={caseDeleting}
        onCancel={() => {
          if (caseDeleting) return;
          setCaseDeleteTarget(null);
        }}
        onConfirm={handleCaseDeleteConfirm}
      >
        {caseDeleteTarget
          ? t('promptCaseLib.deleteConfirm', { name: caseDeleteTarget.name })
          : ''}
      </IpeDeleteConfirmModal>
    </div>
  );
}
